import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { Copy, Calendar as CalIcon, Ticket, LogOut, MessageCircle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useUnreadMessages } from "@/lib/useUnreadMessages";

export default function Dashboard() {
  const { user, logout, loading } = useAuth();
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const { count: unread } = useUnreadMessages();

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
  }, [user, loading, nav]);

  if (loading || !user) return <div className="container-bl py-20">Loading…</div>;

  const copyReferral = () => {
    navigator.clipboard.writeText(user.referral_code || "");
    toast.success("Referral code copied!");
  };

  const cancelBooking = async (id) => {
    try {
      await api.post(`/bookings/${id}/cancel`);
      toast.success("Booking cancelled");
      const { data } = await api.get("/bookings");
      setBookings(data);
    } catch (e) {
      toast.error("Could not cancel booking");
    }
  };

  return (
    <div className="container-bl py-10" data-testid="page-dashboard">
      <Seo title="Dashboard · B&L Rentals" description="Manage your rental bookings and referral code." />
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-sm uppercase tracking-widest text-[#E2AB20] font-semibold">Customer dashboard</div>
          <h1 className="mt-1 text-3xl md:text-4xl font-heading font-bold text-white">Welcome back, {user.name?.split(" ")[0] || "friend"}</h1>
        </div>
        <div className="flex gap-3">
          <Link to="/messages" className="btn-secondary relative" data-testid="dash-messages">
            <MessageCircle size={16} className="mr-2" /> Messages
            {unread > 0 && (
              <span
                data-testid="unread-badge-dashboard"
                className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#E2AB20] text-[#010F27] text-[11px] font-bold leading-none"
              >{unread}</span>
            )}
          </Link>
          <Button variant="outline" onClick={async () => { await logout(); nav("/"); }} data-testid="dash-logout"><LogOut size={16} className="mr-2" /> Sign out</Button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="card-bl p-6" data-testid="referral-card">
          <div className="text-xs uppercase tracking-widest text-[#AEB9C9]">Your referral code</div>
          <div className="mt-2 flex items-center gap-3">
            <div className="text-2xl font-mono font-bold text-[#E2AB20]" data-testid="referral-code">{user.referral_code}</div>
            <Button size="sm" variant="outline" onClick={copyReferral} data-testid="copy-referral"><Copy size={14} /></Button>
          </div>
          <p className="mt-3 text-sm text-[#AEB9C9]">Share your code — when a friend books, you both get 10% off your next trip.</p>
        </div>
        <div className="card-bl p-6">
          <div className="text-xs uppercase tracking-widest text-[#AEB9C9]">Total bookings</div>
          <div className="mt-2 text-3xl font-heading font-bold text-white">{bookings.length}</div>
        </div>
        <div className="card-bl p-6">
          <div className="text-xs uppercase tracking-widest text-[#AEB9C9]">Active member</div>
          <div className="mt-2 text-lg font-medium text-white">{user.email}</div>
          <div className="mt-1 text-xs text-[#AEB9C9]">Role: {user.role}</div>
        </div>
      </div>

      <div className="mt-10">
        <h2 className="text-2xl font-heading font-semibold text-white">Your bookings</h2>
        <div className="mt-4 space-y-3">
          {bookings.length === 0 && <div className="card-bl p-8 text-center text-[#AEB9C9]">No bookings yet. <Link to="/vehicles" className="link-blue">Find a vehicle →</Link></div>}
          {bookings.map((b) => (
            <div key={b.booking_id} className="card-bl p-5 flex items-center gap-4 flex-wrap" data-testid={`booking-row-${b.booking_id}`}>
              {b.vehicle_image && <img src={b.vehicle_image} alt={b.vehicle_name} className="w-24 h-16 rounded-md object-cover" />}
              <div className="flex-1 min-w-[200px]">
                <div className="font-heading font-semibold text-white">{b.vehicle_name}</div>
                <div className="text-sm text-[#AEB9C9] flex items-center gap-2"><CalIcon size={12} /> {b.start_date} → {b.end_date} · {b.days} day{b.days > 1 ? "s" : ""}</div>
              </div>
              <div className="text-right">
                <div className="font-semibold">${b.total?.toFixed(2)}</div>
                <div className={`text-xs mt-1 inline-block px-2 py-0.5 rounded-full ${b.status === "confirmed" ? "bg-green-50 text-green-700" : "bg-gray-100 text-[#AEB9C9]"}`}>{b.status}</div>
              </div>
              {b.status === "confirmed" && (
                <Button variant="outline" size="sm" onClick={() => cancelBooking(b.booking_id)} data-testid={`cancel-${b.booking_id}`}>Cancel</Button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
